import { useEffect, useRef, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Send, Loader2, AlertCircle, X, Plus } from "lucide-react";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SettingsPage() {
  const [form, setForm] = useState({
    recipient_emails: [],
    from_email: "",
    send_on_create: true,
    send_on_complete: true,
    only_high_priority_on_create: false,
  });
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/notifications");
      setForm({ ...data, recipient_emails: data.recipient_emails || [] });
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addEmail = (raw) => {
    const v = (raw || "").trim().toLowerCase();
    if (!v) return false;
    if (!EMAIL_RE.test(v)) {
      toast.error(`"${v}" doesn't look like a valid email`);
      return false;
    }
    if (form.recipient_emails.includes(v)) {
      toast.info(`${v} is already on the list`);
      return false;
    }
    setForm((prev) => ({ ...prev, recipient_emails: [...prev.recipient_emails, v] }));
    return true;
  };

  const handleEmailKey = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      if (addEmail(emailInput)) setEmailInput("");
    } else if (e.key === "Backspace" && !emailInput && form.recipient_emails.length) {
      // Quick-remove last chip on backspace when input is empty
      setForm((prev) => ({ ...prev, recipient_emails: prev.recipient_emails.slice(0, -1) }));
    }
  };

  const removeEmail = (idx) =>
    setForm((prev) => ({
      ...prev,
      recipient_emails: prev.recipient_emails.filter((_, i) => i !== idx),
    }));

  const save = async () => {
    // If there's anything in the input that wasn't committed, take it
    if (emailInput.trim()) {
      if (addEmail(emailInput)) setEmailInput("");
      else return; // bad input; abort save
    }
    setSaving(true);
    try {
      await api.patch("/settings/notifications", form);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (form.recipient_emails.length === 0) return toast.error("Add at least one email first");
    setTesting(true);
    try {
      const { data } = await api.post("/settings/notifications/test");
      const okList = (data.sent_to || []).join(", ");
      if (okList) toast.success(`Test email sent to ${okList}`);
      if (data.errors && data.errors.length) {
        data.errors.slice(0, 3).forEach((err) => toast.error(err));
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <Layout><div className="p-10 text-center text-sm text-gray-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Admin</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Notifications</h1>
          <p className="text-sm text-gray-600 mt-2">
            Email alerts go out the moment an issue is filed or marked complete.
          </p>
        </div>

        <div className="space-y-6">
          {/* Email recipients */}
          <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500">
              <Mail size={12} /> Email addresses
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Send alerts to</Label>

              {/* Chip list */}
              {form.recipient_emails.length > 0 && (
                <div
                  data-testid="recipient-emails-list"
                  className="flex flex-wrap gap-2 pt-1"
                >
                  {form.recipient_emails.map((email, idx) => (
                    <span
                      key={email}
                      data-testid={`recipient-email-chip-${idx}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 text-gray-800 text-sm px-3 py-1 border border-gray-200"
                    >
                      {email}
                      <button
                        type="button"
                        data-testid={`remove-recipient-email-${idx}`}
                        onClick={() => removeEmail(idx)}
                        className="text-gray-500 hover:text-red-600 transition-colors"
                        aria-label={`Remove ${email}`}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add new email row */}
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  data-testid="recipient-email-input"
                  type="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleEmailKey}
                  placeholder="you@company.com"
                />
                <Button
                  type="button"
                  variant="outline"
                  data-testid="add-recipient-email"
                  onClick={() => {
                    if (addEmail(emailInput)) setEmailInput("");
                    inputRef.current?.focus();
                  }}
                >
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Press <kbd className="font-mono">Enter</kbd>, <kbd className="font-mono">,</kbd> or space to add. Anyone on this list will receive new-issue and completion alerts.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">"From" email address</Label>
              <Input
                data-testid="from-email"
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={form.from_email}
                onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                placeholder="alerts@yourdomain.com"
              />
              <p className="text-xs text-gray-500">
                Using <code className="font-mono">onboarding@resend.dev</code> is fine for testing, but
                Resend only delivers it to the address you signed up with. To send to anyone, verify a
                domain at <a className="text-[#0055FF] underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a>.
              </p>
            </div>
          </section>

          {/* Triggers */}
          <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
              When to send
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-sm">A new issue is submitted</p>
                <p className="text-xs text-gray-500 mt-0.5">From any location, any role.</p>
              </div>
              <Switch
                data-testid="toggle-on-create"
                checked={form.send_on_create}
                onCheckedChange={(v) => setForm({ ...form, send_on_create: v })}
              />
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-sm">Only when priority is HIGH</p>
                <p className="text-xs text-gray-500 mt-0.5">Skip emails for medium / low.</p>
              </div>
              <Switch
                data-testid="toggle-high-only"
                checked={form.only_high_priority_on_create}
                onCheckedChange={(v) => setForm({ ...form, only_high_priority_on_create: v })}
                disabled={!form.send_on_create}
              />
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-sm">An issue is marked complete</p>
                <p className="text-xs text-gray-500 mt-0.5">Closes the loop on every job.</p>
              </div>
              <Switch
                data-testid="toggle-on-complete"
                checked={form.send_on_complete}
                onCheckedChange={(v) => setForm({ ...form, send_on_complete: v })}
              />
            </div>
          </section>

          {/* Notice */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
            <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-900">
              <strong>Heads up:</strong> if you haven't verified a domain on Resend yet, test emails
              will only reach the address you signed up with. Verify a domain to send to anyone.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="save-settings"
              onClick={save}
              disabled={saving}
              className="bg-[#0055FF] hover:bg-[#0044CC]"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Save settings"}
            </Button>
            <Button
              data-testid="test-email"
              variant="outline"
              onClick={sendTest}
              disabled={testing || form.recipient_emails.length === 0}
            >
              {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
              Send test email
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
